import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Key, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRY_CONFIG } from '@/utils/countryConfig';
const BACKEND_URL = window.location.origin;

const getAdminAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function EditRestaurantPage() {
  const navigate = useNavigate();
  const { restaurantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    owner_name: '',
    owner_email: '',
    owner_phone: '',
    address: '',
    commission_rate: 5.0,
    status: 'active',
    country: 'India',
    currency: COUNTRY_CONFIG.India.currency,
    currency_symbol: COUNTRY_CONFIG.India.currency_symbol,
    locale: COUNTRY_CONFIG.India.locale,
    timezone: COUNTRY_CONFIG.India.timezone,
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  useEffect(() => {
    fetchRestaurantDetails();
  }, [restaurantId]);

  const fetchRestaurantDetails = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/restaurants/${restaurantId}`,
        { headers: getAdminAuthHeader() }
      );
      const restaurant = response.data;
      setFormData({
        name: restaurant.name || '',
        owner_name: restaurant.owner_name || '',
        owner_email: restaurant.owner_email || '',
        owner_phone: restaurant.owner_phone || '',
        address: restaurant.address || '',
        commission_rate: restaurant.commission_rate || 0.0,
        country: restaurant.country || 'India',
        currency: restaurant.currency || COUNTRY_CONFIG.India.currency,
        currency_symbol: restaurant.currency_symbol || COUNTRY_CONFIG.India.currency_symbol,
        status: restaurant.status || 'active',
        locale: restaurant.locale || COUNTRY_CONFIG.India.locale,
        timezone: restaurant.timezone || COUNTRY_CONFIG.India.timezone,
      });
    } catch (error) {
      console.error('Error fetching restaurant:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load restaurant details');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateNewPassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#$';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const handlePasswordUpdate = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    try {
      // Call backend to update password
      await axios.post(
        `${BACKEND_URL}/api/admin/restaurants/${restaurantId}/reset-password`,
        { new_password: newPassword },
        { headers: getAdminAuthHeader() }
      );

      toast.success('Password updated successfully!');
      setNewPassword('');
      setShowPasswordSection(false);
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error(error.response?.data?.detail || 'Failed to update password');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/restaurants/${restaurantId}`,
        formData,
        { headers: getAdminAuthHeader() }
      );

      toast.success('Restaurant updated successfully!');
      navigate(`/admin/restaurants/${restaurantId}`);
    } catch (error) {
      console.error('Error updating restaurant:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to update restaurant';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading restaurant details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`/admin/restaurants/${restaurantId}`)}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Edit Restaurant</h1>
              <p className="text-sm text-gray-600">Update restaurant information</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Restaurant Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>
                
                <div>
                  <Label htmlFor="name">Restaurant Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Taste of India"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="address">Full Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main St, Sydney, NSW 2000"
                  />
                </div>
              </div>

              {/* Owner Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Owner / Contact Details</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="owner_name">Owner Name *</Label>
                    <Input
                      id="owner_name"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                      placeholder="John Smith"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="owner_phone">Phone Number *</Label>
                    <Input
                      id="owner_phone"
                      type="tel"
                      value={formData.owner_phone}
                      onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                      placeholder="+61 400 123 456"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="owner_email">Email Address *</Label>
                  <Input
                    id="owner_email"
                    type="email"
                    value={formData.owner_email}
                    onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                    placeholder="owner@restaurant.com"
                    required
                  />
                </div>
              </div>
              {/* Country & Currency */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">
                  Country & Currency
                </h3>

                <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="country">Country *</Label>

                  <Select
                    value={formData.country}
                    onValueChange={(country) => {
                      const config = COUNTRY_CONFIG[country];

                      setFormData({
                        ...formData,
                        country,
                        currency: config.currency,
                        currency_symbol: config.currency_symbol,
                        locale: config.locale,
                        timezone: config.timezone,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Country" />
                    </SelectTrigger>

                    <SelectContent>
                      {Object.keys(COUNTRY_CONFIG).map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                  <div>
                    <Label htmlFor="currency">Currency Code *</Label>
                    <Input
                      id="currency"
                      value={formData.currency}
                      readOnly
                      className="bg-gray-100"
                    />
                  </div>

                  <div>
                    <Label htmlFor="currency_symbol">Currency Symbol *</Label>
                   <Input
                      id="currency_symbol"
                      value={formData.currency_symbol}
                      readOnly
                      className="bg-gray-100"
                    />
                  </div>
                  <div>
                    <Label htmlFor="locale">Locale *</Label>
                    <Input
                      id="locale"
                      value={formData.locale}
                      readOnly
                      className="bg-gray-100"
                    />
                  </div>
                </div>

                <div className="bg-gray-50 border rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    Currency Preview
                  </p>

                    <p className="text-2xl font-bold mt-1">
                      {
                        (() => {
                          try {
                            return new Intl.NumberFormat(
                              formData.locale || "en-AU",
                              {
                                style: "currency",
                                currency: formData.currency || "AUD",
                              }
                            ).format(1250);
                          } catch (error) {
                            return `${formData.currency_symbol}1,250.00`;
                          }
                        })()
                      }
                    </p>

                  <p className="text-xs text-gray-500 mt-1">
                    This currency will be used across invoices,
                    reports, orders, menu pricing, and billing.
                  </p>
                </div>
              </div>
              {/* Commission & Status */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Commission & Status</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="commission_rate">Commission Rate (%) *</Label>
                    <Input
                      id="commission_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.commission_rate}
                      onChange={(e) => setFormData({ ...formData, commission_rate: parseFloat(e.target.value) || 0 })}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">% of total billing charged as commission</p>
                  </div>

                  <div>
                    <Label htmlFor="status">Status *</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Password Management */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="text-lg font-semibold">Password Management</h3>
                  {!showPasswordSection && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPasswordSection(true)}
                    >
                      <Key size={16} className="mr-2" />
                      Change Password
                    </Button>
                  )}
                </div>

                {showPasswordSection && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-4">
                    <div className="flex items-start gap-2">
                      <Key size={20} className="text-yellow-600 mt-1" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-yellow-900 mb-2">
                          Reset Restaurant Admin Password
                        </p>
                        <p className="text-xs text-yellow-700">
                          This will change the login password for the restaurant admin user. 
                          Make sure to share the new password with the restaurant owner.
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="new_password">New Password</Label>
                      <div className="flex gap-2">
                        <Input
                          id="new_password"
                          type="text"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Enter new password (min 8 characters)"
                          className="font-mono"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={generateNewPassword}
                        >
                          Generate
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum 8 characters required
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowPasswordSection(false);
                          setNewPassword('');
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={handlePasswordUpdate}
                        disabled={!newPassword || newPassword.length < 8}
                        className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                      >
                        Update Password
                      </Button>
                    </div>
                  </div>
                )}

                {!showPasswordSection && (
                  <p className="text-sm text-gray-600">
                    Click "Change Password" to reset the restaurant admin login password
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/admin/restaurants/${restaurantId}`)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-red-600 hover:bg-red-700 flex-1"
                  disabled={saving}
                >
                  {saving ? 'Saving Changes...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}
